import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, CircularProgress, Typography, Paper, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMaps } from '../Maps/MapsProvider';
import api from '../../utils/axios';
import SensorService from '../../services/sensorService';
import MapFallback from '../Maps/MapFallback';

const MapComponent = ({ bins, onMarkerClick }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef(new Map());
  const didFitBoundsRef = useRef(false);
  const mapsConfig = useMaps();
  const [mapError, setMapError] = useState(null);

  useEffect(() => {
    if (!mapRef.current) return;
    
    if (!window.google) {
      setMapError('Google Maps API not loaded');
      return;
    }

    try {
      // Initialize map
      const map = new window.google.maps.Map(mapRef.current, {
        center: mapsConfig.defaultCenter,
        zoom: mapsConfig.defaultZoom,
        mapId: mapsConfig.mapId,
        ...mapsConfig.mapOptions
      });
      mapInstanceRef.current = map;
      setMapError(null);
    } catch (error) {
      console.error('Error initializing Google Maps:', error);
      setMapError(error.message || 'Failed to initialize map');
    }

    // Clean up markers on unmount
    return () => {
      markersRef.current.forEach(marker => {
        if (marker) {
          marker.setMap(null);
        }
      });
      markersRef.current.clear();
      didFitBoundsRef.current = false;
    };
  }, [mapsConfig]);

  useEffect(() => {
    if (!mapInstanceRef.current || !bins || !window.google) return;

    const map = mapInstanceRef.current;
    const previousCenter = typeof map.getCenter === 'function' ? map.getCenter() : null;
    const previousZoom = typeof map.getZoom === 'function' ? map.getZoom() : null;

    const activeMarkerIds = new Set();

    // Add new markers
    bins.forEach(bin => {
      if (!bin.location?.coordinates) return;

      const position = {
        lat: bin.location.coordinates[1],
        lng: bin.location.coordinates[0]
      };

      const markerId = bin.binId || bin._id;
      if (!markerId) {
        return;
      }

      // Get sensor data for display
      const sensorData = SensorService.formatSensorData(bin.sensorData);
      const displayLevel = sensorData.hasData ? sensorData.levelValue : bin.currentCapacity;
      const markerColor = sensorData.hasData ? 
        SensorService.getFillLevelColor(sensorData.levelValue) : 
        getStatusColor(bin.status);

      // Create marker element
      const markerElement = document.createElement('div');
      markerElement.className = 'custom-marker';
      markerElement.innerHTML = `
        <div style="
          background-color: ${markerColor};
          width: 40px;
          height: 40px;
          border-radius: 8px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 20px;
          position: relative;
        ">
          ${getStatusIcon(bin.status)}
          ${sensorData.hasData ? `
            <div style="
              position: absolute;
              top: -8px;
              right: -8px;
              background-color: #10b981;
              width: 16px;
              height: 16px;
              border-radius: 50%;
              border: 2px solid white;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 8px;
            ">
              📡
            </div>
          ` : ''}
          ${bin.status !== 'maintenance' ? `
            <div style="
              position: absolute;
              bottom: -20px;
              left: 50%;
              transform: translateX(-50%);
              background-color: ${markerColor};
              color: white;
              padding: 2px 6px;
              border-radius: 10px;
              font-size: 10px;
              white-space: nowrap;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            ">
              ${displayLevel !== null ? displayLevel + '%' : 'N/A'}
            </div>
          ` : ''}
        </div>
      `;

      try {
        let marker = markersRef.current.get(markerId);

        if (!marker) {
          marker = new window.google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            content: markerElement,
            title: `Bin ID: ${bin.binId}\nStatus: ${bin.status}\nType: ${bin.type}\nSensor: ${sensorData.hasData ? `${sensorData.level} (${sensorData.lastUpdate})` : 'No sensor data'}`
          });

          marker.addListener('click', () => {
            if (onMarkerClick) {
              onMarkerClick(bin);
            }
          });

          markersRef.current.set(markerId, marker);
        } else {
          marker.position = position;
          marker.content = markerElement;
          marker.title = `Bin ID: ${bin.binId}\nStatus: ${bin.status}\nType: ${bin.type}\nSensor: ${sensorData.hasData ? `${sensorData.level} (${sensorData.lastUpdate})` : 'No sensor data'}`;
        }

        activeMarkerIds.add(markerId);
      } catch (error) {
        console.error('Error creating marker:', error);
      }
    });

    // Remove markers that are no longer present
    markersRef.current.forEach((marker, id) => {
      if (!activeMarkerIds.has(id)) {
        if (marker) {
          marker.map = null;
        }
        markersRef.current.delete(id);
      }
    });

    if (bins.length === 0) {
      didFitBoundsRef.current = false;
    }

    // Fit bounds if there are markers
    if (markersRef.current.size > 0) {
      if (!didFitBoundsRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        markersRef.current.forEach(marker => {
          bounds.extend(marker.position);
        });
        map.fitBounds(bounds);
        didFitBoundsRef.current = true;
      } else if (previousCenter) {
        map.setCenter(previousCenter);
        if (typeof previousZoom === 'number') {
          map.setZoom(previousZoom);
        }
      }
    }
  }, [bins, onMarkerClick]);

  if (mapError) {
    return <MapFallback error={mapError} onRetry={() => window.location.reload()} />;
  }

  return (
    <Box
      ref={mapRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '500px',
        borderRadius: 1,
        overflow: 'hidden'
      }}
    />
  );
};

const getStatusColor = (status) => {
  const colors = {
    empty: '#4CAF50',
    partial: '#FFA726',
    full: '#F44336',
    maintenance: '#9E9E9E'
  };
  return colors[status?.toLowerCase()] || colors.maintenance;
};

const getStatusIcon = (status) => {
  // Using UTF-8 icons for dustbin
  const icons = {
    empty: '🗑️',
    partial: '🗑️',
    full: '🗑️',
    maintenance: '⚠️'
  };
  return icons[status?.toLowerCase()] || '🗑️';
};

const DashboardMapView = () => {
  const [bins, setBins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sensorStatus, setSensorStatus] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const pollingIntervalRef = useRef(null);
  const binsRefreshIntervalRef = useRef(null);

  const fetchBins = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.get('/bins');
      setBins(response.data);
      
      // Check sensor service status
      const status = await SensorService.checkSensorServiceStatus();
      setSensorStatus(status);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching bins:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchBins();

    const handleBinsUpdate = () => fetchBins({ silent: true });
    window.addEventListener('binsUpdated', handleBinsUpdate);

    binsRefreshIntervalRef.current = setInterval(() => {
      fetchBins({ silent: true });
    }, 10000);

    // Start real-time polling for sensor data
    pollingIntervalRef.current = SensorService.startRealTimePolling(
      (sensorData) => {
        if (sensorData.status === 'success') {
          // Refresh bins to get updated sensor data
          fetchBins({ silent: true });
        }
      },
      30000 // Poll every 30 seconds
    );

    return () => {
      window.removeEventListener('binsUpdated', handleBinsUpdate);
      if (binsRefreshIntervalRef.current) {
        clearInterval(binsRefreshIntervalRef.current);
      }
      if (pollingIntervalRef.current) {
        SensorService.stopRealTimePolling(pollingIntervalRef.current);
      }
    };
  }, [fetchBins]);

  const handleMarkerClick = (bin) => {
    navigate(`/bins/${bin._id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      width: '100%', 
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Sensor Status Indicator */}
      <Box sx={{ 
        position: 'absolute', 
        top: { xs: 12, md: 16 }, 
        right: { xs: 12, md: 16 }, 
        zIndex: 1000 
      }}>
        <Chip
          icon={<span style={{ fontSize: '12px' }}>📡</span>}
          label={sensorStatus ? 'Sensors Online' : 'Sensors Offline'}
          color={sensorStatus ? 'success' : 'error'}
          size="small"
          variant="filled"
          sx={{ 
            backgroundColor: sensorStatus ? '#10b981' : '#ef4444',
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: '0.75rem', md: '0.875rem' }
          }}
        />
      </Box>
      <MapComponent bins={bins} onMarkerClick={handleMarkerClick} />
    </Box>
  );
};

export default DashboardMapView; 